import React, { useEffect, useMemo, useRef, useState } from "react";

export interface ClientOption {
  erp_ref: string;
  name: string | null;
}

interface Props {
  id: string;
  options: ClientOption[];
  onAdd: (erpRef: string) => void;
  disabled?: boolean;
  loading?: boolean;
  placeholder?: string;
}

const labelOf = (c: ClientOption): string =>
  c.name ? `${c.name} (${c.erp_ref})` : c.erp_ref;

const MAX_RESULTS = 50;

/**
 * Typeahead that ADDS an ERP client to a list (the visibility "Clientes B2B
 * com acesso" allowlist). Same interaction as the campaigns ResellerCombobox
 * (keyboard nav + ARIA listbox) but add-oriented: selecting clears the input so
 * the operator can add several; free text is never accepted.
 */
const ClientSearchCombobox: React.FC<Props> = ({
  id,
  options,
  onAdd,
  disabled,
  loading,
  placeholder,
}) => {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onDocMouseDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const pool = q
      ? options.filter((o) =>
          `${o.erp_ref} ${o.name ?? ""}`.toLowerCase().includes(q),
        )
      : options;
    return pool.slice(0, MAX_RESULTS);
  }, [query, options]);

  const select = (c: ClientOption) => {
    onAdd(c.erp_ref);
    setQuery("");
    setOpen(false);
    setHighlight(0);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!open) {
        setOpen(true);
        setHighlight(0);
      } else {
        setHighlight((h) => Math.min(h + 1, filtered.length - 1));
      }
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      if (open && filtered[highlight]) {
        e.preventDefault();
        select(filtered[highlight]);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const activeOption = open ? filtered[highlight] : undefined;

  return (
    <div ref={rootRef} className="relative">
      <input
        id={id}
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-controls={`${id}-listbox`}
        aria-autocomplete="list"
        aria-activedescendant={
          activeOption ? `${id}-opt-${activeOption.erp_ref}` : undefined
        }
        autoComplete="off"
        disabled={disabled}
        value={query}
        placeholder={
          placeholder ??
          (loading ? "A carregar clientes…" : "Procurar cliente ERP…")
        }
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
          setHighlight(0);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        onBlur={() => setQuery("")}
        className="input w-full"
      />
      {open && !disabled && (
        <ul
          id={`${id}-listbox`}
          role="listbox"
          aria-label="Clientes ERP"
          className="absolute z-50 mt-1 max-h-64 w-full overflow-auto rounded-lg border border-gray-200 bg-white shadow-lg"
        >
          {filtered.length === 0 ? (
            <li className="px-3 py-2 text-sm text-gray-500">Sem resultados.</li>
          ) : (
            filtered.map((c, i) => (
              <li
                key={c.erp_ref}
                id={`${id}-opt-${c.erp_ref}`}
                role="option"
                aria-selected={i === highlight}
                onMouseEnter={() => setHighlight(i)}
                onMouseDown={(e) => {
                  // Keep focus on the input so blur doesn't close first.
                  e.preventDefault();
                  select(c);
                }}
                className={
                  "cursor-pointer px-3 py-2 text-sm text-gray-800 " +
                  (i === highlight ? "bg-blue-50" : "")
                }
              >
                {labelOf(c)}
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
};

export default ClientSearchCombobox;
